import { ConversionTracking, EKHO_EVENTS } from '@ekhodealer/ekho-common/analytics';

import type {
  SdkConfig,
  SdkMarketingAttribution,
  SdkModuleContext,
  SdkPageContext,
} from '@ekhodealer/ekho-common';
import type { Logger } from './diagnostics';
import type { LoadedModule } from './module-loader';
import type { Deferred } from './namespace';
import type { EkhoNamespace } from './types';

import { initCheckoutCapability } from '../capabilities/checkout';
import { startPageWatcher } from '../capabilities/page-context/watcher';
import { readScriptConfig, resolveApiBaseUrl, resolveEndpoint } from '../config/endpoint';
import { ConfigFetchError, fetchConfig } from '../config/fetch';
import { initAnalytics, resetAnalytics, track, trackPageViewBeacon } from '../utils/analytics';
import { captureAttribution, clearAttribution, persistAttribution } from '../utils/attribution';
import { createEventBus } from '../utils/bus';
import { clearIdentity, persistIdentity } from '../utils/identity';

import { activate } from './activate';
import {
  consentStatus,
  grantConsent,
  initConsent,
  onConsentRevoked,
  revokeConsent,
  whenTrackingAllowed,
} from './consent';
import { createLogger, dispatchErrorEvent, injectPreconnect, mark } from './diagnostics';
import { loadModule } from './module-loader';
import { MODULE_REGISTRY } from './module-registry';
import { createNamespace } from './namespace';
import { hasNativeChatSurface } from './native-chat-surface';
import { installEkhoChatShim } from './shim';

interface BootstrapOptions {
  ekho: EkhoNamespace;
  ready: Deferred;
  checkoutBound: Deferred;
  key: string;
  endpoint: string;
  apiBaseUrl: string;
  attribution: SdkMarketingAttribution;
  log: Logger;
}

/** Load each module whose descriptor returns a bundle URL. Failures are logged and skipped. */
async function loadEnabledModules(
  config: SdkConfig,
  context: SdkModuleContext,
  log: Logger,
): Promise<LoadedModule[]> {
  const loaded: LoadedModule[] = [];
  for (const descriptor of MODULE_REGISTRY) {
    const url = descriptor.getBundleUrl(config);
    if (!url) continue;

    const mod = await loadModule(url, config, context);
    if (mod) {
      loaded.push(mod);
      mark(`ekho:module-${descriptor.name}-loaded`);
      log(`${descriptor.name} module loaded`, url);
    } else {
      dispatchErrorEvent('module-load', descriptor.name);
      log(`${descriptor.name} module disallowed or failed to load`, url);
    }
  }
  return loaded;
}

function handleBootstrapError(err: unknown, options: BootstrapOptions): void {
  const { ekho, ready, log } = options;
  if (err instanceof ConfigFetchError && err.isClientError) {
    // Invalid key — reject ready, load no modules.
    dispatchErrorEvent('config', 'invalid_key');
    ready.reject(err);
    return;
  }
  // Transient failure — resolve ready with no modules so host code never hangs.
  // Config is unreachable, so `requireConsent` can't be resolved. Fall back to
  // the loose default the SDK used before the consent gate (persist identity +
  // first-touch attribution), so a config blip doesn't mint a fresh anonymous id
  // on every load or drop attribution for the default (consent-not-required)
  // fleet. A consent-required dealer whose config is down loads no modules here
  // anyway.
  dispatchErrorEvent('config', 'fetch_failed');
  persistIdentity();
  persistAttribution();
  activate(ekho, []);
  ready.resolve();
  log('config fetch failed; SDK deactivated', err instanceof Error ? err.message : 'unknown error');
}

/**
 * Wire the consent gate and the tracking side effects it governs. Returns the
 * page-watcher callback (always emits `page:context`, gates the analytics
 * page-views) and cleanups to register with `activate()`.
 */
function initConsentGate(
  ekho: EkhoNamespace,
  config: SdkConfig,
  attribution: SdkMarketingAttribution,
  beacon: { key: string; apiBaseUrl: string },
  log: Logger,
): { onPageContext: (ctx: SdkPageContext) => void; cleanups: Array<() => void> } {
  const cleanups: Array<() => void> = [];

  // Opt-in gating: only `requireConsent: true` collects consent first. Absent
  // or `false` tracks on load, so a dealer with no SDK setting is unaffected.
  initConsent(config.requireConsent ?? false);

  // Bind the real consent API before activate() so a grant/revoke the CMP
  // buffered on the queue stub replays here.
  ekho.consent = {
    grant: () => grantConsent(),
    revoke: () => revokeConsent(),
    status: () => consentStatus(),
  };

  // Whether tracking side effects have run. Gates page-view emission so
  // buffered views don't fire before analytics init, and stop after a revoke.
  let tracking = false;
  // Whether consent has been decided at least once. Page-views buffer only
  // during the initial pending window; after a decision, a view arriving while
  // tracking is off is dropped, so a re-grant never replays opted-out views.
  let settled = false;

  const pageViewBuffer: SdkPageContext[] = [];
  const firePageViewAnalytics = (ctx: SdkPageContext): void => {
    if (ConversionTracking.isInitialized()) {
      ConversionTracking.trackCustomEvent(EKHO_EVENTS.PAGE_VIEW.canonical, {
        ['page_title']: ctx.title,
        ['page_location']: ctx.url,
      });
    }
    track(EKHO_EVENTS.SDK_PAGE_VIEW, {
      ['page_title']: ctx.title,
      ['page_location']: ctx.url,
    });
    // Ekho's own page-view record, which replaces `sdk_page_view` on both
    // metered vendors once the warehouse views cut over. It belongs on this
    // line rather than anywhere cheaper to reach, because everything this
    // function already guarantees is inherited for free and nothing else
    // guarantees the same: SPA navigation reaches page views only through
    // here, the consent buffer and the `tracking` flag above decide whether
    // this runs at all, and firing beside `track` is what leaves the two
    // counts unable to drift.
    trackPageViewBeacon(ctx.url, ctx.title);
  };

  onConsentRevoked(() => {
    tracking = false;
    settled = true;
    pageViewBuffer.length = 0;
    clearIdentity();
    clearAttribution();
    resetAnalytics();
    // Tear down conversion tracking too, or GA4 / ad-pixel state stays
    // initialized and callsites guarded on `isInitialized()` keep firing after
    // opt-out. `reset()` is a no-op when it was never initialized.
    if (config.marketing) ConversionTracking.reset();
    ekho._bus.fire('consent:revoked', undefined);
  });

  // Conversion-tracking teardown is wired up front so a late grant is still
  // torn down by `Ekho.destroy()`; `init()` itself runs inside startTracking.
  if (config.marketing) {
    cleanups.push(() => ConversionTracking.reset());
  }

  // The tracking side effects, deferred behind the gate. Runs now when
  // bypassing, or on `grant()` when consent is required.
  const startTracking = (): void => {
    const persisted = persistIdentity();
    persistAttribution();
    // A revoke clears the memoized identity, so a re-grant mints fresh ids.
    // Point the exposed attribution and the analytics seed at the values
    // actually persisted, so PostHog's distinct_id can't drift from the
    // `ekho_aid` cookie that Segment and the checkout handoff read.
    attribution.anonymousId = persisted.anonymousId;
    attribution.sessionId = persisted.sessionId;
    if (config.marketing) {
      ConversionTracking.init({
        programOwnerId: config.programOwnerId,
        domainOwnerSvpId: config.svpId,
        ekhoApp: 'sdk',
        marketingResource: config.marketing,
        userDataConsent: false,
      });
    }
    // Skipped when config-disabled, so `track(...)` stays a no-op and a
    // disabled SDK emits nothing even after consent.
    if (!config.disabled) {
      initAnalytics({
        anonymousId: persisted.anonymousId,
        programOwnerId: config.programOwnerId,
        domainOwnerSvpId: config.svpId,
        apiBaseUrl: beacon.apiBaseUrl,
        key: beacon.key,
        log,
      });
    }
    // Flush page-views buffered while consent was pending, now that the
    // destinations are initialized, then fire live from here.
    const buffered = pageViewBuffer.splice(0);
    tracking = true;
    settled = true;
    for (const ctx of buffered) firePageViewAnalytics(ctx);
    ekho._bus.fire('consent:granted', undefined);
  };
  whenTrackingAllowed(startTracking);

  const onPageContext = (ctx: SdkPageContext): void => {
    ekho._bus.fire('page:context', ctx);
    if (tracking) firePageViewAnalytics(ctx);
    else if (!settled) pageViewBuffer.push(ctx);
    // Otherwise consent is decided and tracking is off (revoked), so drop.
  };

  return { onPageContext, cleanups };
}

async function bootstrap(options: BootstrapOptions): Promise<void> {
  const { ekho, ready, checkoutBound, key, endpoint, apiBaseUrl, attribution, log } = options;
  try {
    const config = await fetchConfig({ endpoint, key });
    mark('ekho:config-fetched');
    ekho.config = config;

    const { onPageContext, cleanups } = initConsentGate(
      ekho,
      config,
      attribution,
      { key, apiBaseUrl },
      log,
    );

    // Start page watcher — emits page:context for chat and other capabilities,
    // and (once consent allows) page views to GA4 + PostHog/Segment.
    const stopPageWatcher = startPageWatcher(onPageContext);
    cleanups.push(stopPageWatcher);

    const context: SdkModuleContext = {
      attribution,
      bus: ekho._bus,
      apiBaseUrl,
      conversionTracking: ConversionTracking,
    };

    // Built-in capabilities. Bind BEFORE activate() so any host calls buffered
    // against the queue-backed stubs (see namespace.ts) replay against the real
    // API. Skipped when the whole SDK is disabled by config.
    if (!config.disabled) {
      const checkout = initCheckoutCapability(config, context);
      if (checkout) {
        ekho.checkout = checkout.api;
        cleanups.push(checkout.cleanup);
      }
    }

    // Released here rather than alongside `ready`: checkout is bound (or
    // decided against) at this point, and the module loading below has no
    // loader-side timeout, so a stalled chat or express bundle must not hold
    // up a buy click the checkout API could already serve.
    checkoutBound.resolve();

    const loaded = config.disabled ? [] : await loadEnabledModules(config, context, log);
    activate(ekho, loaded, cleanups);

    mark('ekho:ready');
    ready.resolve();
    log('ready', {
      disabled: config.disabled,
      consent: consentStatus(),
      modules: loaded.map((m) => m.module.name),
    });
  } catch (err) {
    handleBootstrapError(err, options);
  }
}

export function main(): void {
  // Double-include guard — dealers frequently paste the snippet twice.
  if (window.Ekho?._loaded) return;

  const scriptConfig = readScriptConfig();
  const log = createLogger(scriptConfig.debug);
  mark('ekho:loader-start');

  const bus = createEventBus();
  const { ekho, ready, checkoutBound } = createNamespace(bus);
  ekho._loaded = true;
  window.Ekho = ekho;

  // A host page that renders the widget itself owns `window.EkhoChat` and the
  // `__ekhoPending` buffer. Installing the shim would replace that global with
  // one delegating to a chat module deliberately left unloaded, silently
  // disabling the host's own widget.
  if (!hasNativeChatSurface()) {
    installEkhoChatShim(ekho, log);
  }
  injectPreconnect(scriptConfig.nonce);

  const attribution = captureAttribution();
  ekho.attribution = attribution;
  bus.emit('attribution:ready', attribution);

  if (!scriptConfig.key) {
    dispatchErrorEvent('config', 'missing_key');
    ready.reject(new Error('Ekho: missing data-key on script tag'));
    return;
  }
  const apiBaseUrl = resolveApiBaseUrl(scriptConfig.key, scriptConfig.endpoint);
  const endpoint = resolveEndpoint(scriptConfig.key, scriptConfig.endpoint);
  if (!apiBaseUrl || !endpoint) {
    dispatchErrorEvent('config', 'invalid_key');
    ready.reject(new Error('Ekho: could not resolve config endpoint for key'));
    return;
  }

  void bootstrap({
    ekho,
    ready,
    checkoutBound,
    key: scriptConfig.key,
    endpoint,
    apiBaseUrl,
    attribution,
    log,
  });
}
